import { FilterOptionsState } from '@mui/material';
import fuzzy from 'fuzzy';
import { useCallback, useMemo } from 'react';
import { Partner, useCurrentUserProfile, useGroupRules, usePartners } from 'src/graphql';
import { filterPartnersByAccess } from 'src/graphql/entities/partner/partner-access';
import { useGroupRulesStore } from 'src/store/group-rules';

/**
 * Using `null` as id for multi-tenancy mode implicitly resets
 * selected partner id, when picking this option.
 * This removes the need to explicitly compare `selectedPartnerId` in store
 * to multi-tenancy option id in every place where we need to use selected partner's id.
 */
const MULTITENANCY_OPTION = {
  label: 'Multi-Tenancy Mode',
  icon: 'all_inclusive',
  id: null,
};

export interface PartnerOption {
  id: number | null;
  label: string;
  currentPartner?: 'yes' | 'no';
  icon?: string;
  rawPartner?: Partner;
}

const fuzzySearchOptions = {
  extract: (it: PartnerOption) => it.label,
};

export function usePartnersDialogOptions({ currentPartner }: { currentPartner: boolean }) {
  const { partners } = usePartners();
  const { user } = useCurrentUserProfile();
  const { groupRules } = useGroupRules();
  const rulesByPartner = useGroupRulesStore((s) => s.rulesByPartner);

  const partnersOptions = useMemo(() => {
    if (!partners) return [];

    const filtered = filterPartnersByAccess({
      partners,
      groupRules,
      user,
      rulesByPartner,
      currentPartnerOnly: currentPartner,
    });

    const options: PartnerOption[] = filtered.map((partner) => ({
      id: partner.partnerId,
      label: partner.businessName,
      currentPartner: partner.currentPartner,
      rawPartner: partner,
    }));

    options.unshift(MULTITENANCY_OPTION);
    return options;
  }, [partners, currentPartner, groupRules, user, rulesByPartner]);

  const filterFn = useCallback(
    (options: PartnerOption[], state: FilterOptionsState<PartnerOption>) => {
      const results = fuzzy
        .filter(state.inputValue, options, fuzzySearchOptions)
        .map((it) => it.original);

      if (!results.find((it) => it.id === MULTITENANCY_OPTION.id)) {
        // Ensure that MULTITENANCY_OPTION is always part of the search results
        results.unshift(MULTITENANCY_OPTION);
      }
      return results;
    },
    [],
  );

  return {
    partnersOptions,
    filterFn,
  };
}
